// src/pages/Welcome.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const sanitizeStateCode = (v) => String(v || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
const isProfileComplete = (f) =>
  !!(String(f?.shop_name || '').trim() && /^[A-Z]{2}$/.test(sanitizeStateCode(f?.state_code)));

export default function Welcome() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    shop_name: '',
    shop_address: '',
    shop_gstin: '',
    state_code: '',
  });
  const [status, setStatus] = useState(null); // { type: 'error' | 'success', message: string }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [originLocked, setOriginLocked] = useState(false);
  const [firstSaleInfo, setFirstSaleInfo] = useState(null);

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoBlobUrl, setLogoBlobUrl] = useState('');
  const fileInputRef = useRef(null);

  const [loadFailed, setLoadFailed] = useState(false);
  const [probingLock, setProbingLock] = useState(false);

  const displayLogo = logoPreview || logoBlobUrl;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await reloadAll(cancelled);
    })();
    return () => {
      cancelled = true;
      setLogoPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      setLogoBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
    };
  }, []);

  async function reloadAll(cancelledFlag) {
    try {
      await loadProfile(cancelledFlag);
      await refreshLogo();
      if (!cancelledFlag) setLoadFailed(false);
      if (!cancelledFlag) setStatus(null);
    } catch (e) {
      if (!cancelledFlag) {
        setLoadFailed(true);
        setStatus({ type: 'error', message: 'Could not load shop profile. Please try again.' });
      }
    } finally {
      if (!cancelledFlag) setLoading(false);
    }
  }

  async function loadProfile(cancelledFlag) {
    const { data } = await api.get('/settings');
    const nextForm = {
      shop_name: data.shop_name || '',
      shop_address: data.shop_address || '',
      shop_gstin: data.shop_gstin || '',
      state_code: sanitizeStateCode(data.state_code ?? data.origin_state ?? ''),
    };
    setForm(nextForm);
    setStatus(null);
    if (isProfileComplete(nextForm)) {
      try { localStorage.setItem('shop_profile_complete', '1'); } catch {}
      if (!cancelledFlag) navigate('/dashboard', { replace: true });
      return;
    }

    // Lightweight lock probe; ignore errors to avoid rate limits
    setProbingLock(true);
    try {
      const salesRes = await api.get('/sales', { params: { limit: 1 }, validateStatus: () => true });
      const rows = Array.isArray(salesRes.data) ? salesRes.data : [];
      if (salesRes.status === 200 && rows.length > 0) {
        setOriginLocked(true);
        const s0 = rows[0] || {};
        setFirstSaleInfo({
          invoice_no: s0.invoice_no || '',
          created_at: s0.created_at || s0.sold_at || '',
        });
      } else {
        setOriginLocked(false);
        setFirstSaleInfo(null);
      }
    } catch { /* ignore lock probe errors */ }
    finally { setProbingLock(false); }
  }

  async function refreshLogo() {
    try {
      const res = await api.get('/settings/logo', {
        responseType: 'blob',
        params: { v: Date.now() },
      });
      const blobUrl = URL.createObjectURL(res.data);
      setLogoBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return blobUrl;
      });
    } catch {
      setLogoBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
    }
  }

  function onFileChange(e) {
    const file = e.target.files?.[0];
    setLogoFile(file || null);
    setStatus(null);
    setLogoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return file ? URL.createObjectURL(file) : '';
    });
  }

  async function uploadLogo() {
    if (!logoFile) {
      setStatus({ type: 'error', message: 'Choose a logo to upload.' });
      return;
    }
    setUploading(true);
    setStatus(null);
    try {
      const fd = new FormData();
      fd.append('logo', logoFile);
      await api.post('/settings/logo', fd);
      await refreshLogo();
      setStatus({ type: 'success', message: 'Logo uploaded.' });
      setLogoFile(null);
      setLogoPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to upload logo.';
      setStatus({ type: 'error', message: msg });
    } finally {
      setUploading(false);
    }
  }

  async function saveProfile(e) {
    e.preventDefault();
    setStatus(null);

    const trimmedName = form.shop_name.trim();
    const trimmedAddr = form.shop_address.trim();
    const stateCode = sanitizeStateCode(form.state_code);

    if (!trimmedName) {
      setStatus({ type: 'error', message: 'Shop name is required.' });
      return;
    }
    if (!originLocked && !/^[A-Z]{2}$/.test(stateCode)) {
      setStatus({ type: 'error', message: 'Enter a valid 2-letter state code (e.g., OD, KA, TN).' });
      return;
    }

    const payload = {
      ...form,
      shop_name: trimmedName,
      shop_address: trimmedAddr,
      state_code: stateCode,
    };

    if (originLocked) {
      delete payload.state_code;
    }

    setSaving(true);
    try {
      await api.put('/settings', payload);
      try { localStorage.setItem('shop_profile_complete', '1'); } catch {}
      setStatus({ type: 'success', message: 'Shop profile saved.' });
      setLoadFailed(false);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const code = err?.response?.status;
      const msg = err?.response?.data?.error || (code === 429 || code === 503
        ? 'Server is busy. Please wait a moment and retry.'
        : 'Could not save shop profile.');
      setStatus({ type: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="welcome-hero">
      <div className="welcome-card">
        <header className="welcome-head">
          <div>
            <div className="welcome-eyebrow">Welcome</div>
            <h1>Shop Profile</h1>
            <p className="welcome-sub">Set up your shop details so invoices and GST calculations stay correct.</p>
          </div>
          <div className="welcome-badge">
            <span className="dot" />
            <span>Signed in</span>
          </div>
        </header>

        {status && (
          <div className={`welcome-alert ${status.type === 'error' ? 'alert-error' : 'alert-success'}`}>
            {status.message}
          </div>
        )}

        {loading ? (
          <div className="loading">Loading shop profile…</div>
        ) : (
          <form className="welcome-form" onSubmit={saveProfile}>
            <div className="grid two">
              <div className="field">
                <label>Shop Name<span className="req">*</span></label>
                <input
                  type="text"
                  value={form.shop_name}
                  onChange={(e) => setForm({ ...form, shop_name: e.target.value })}
                  placeholder="Anvi Electronics"
                  required
                />
              </div>
              <div className="field">
                <label>GSTIN</label>
                <input
                  type="text"
                  value={form.shop_gstin}
                  onChange={(e) => setForm({ ...form, shop_gstin: e.target.value })}
                  placeholder="GSTIN"
                />
              </div>
            </div>

            <div className="field">
              <label>Address</label>
              <textarea
                rows={4}
                value={form.shop_address}
                onChange={(e) => setForm({ ...form, shop_address: e.target.value })}
                placeholder="Door no, street, city, pin code"
              />
            </div>

            <div className="field">
              <label>State Code<span className="req">*</span></label>
              {!originLocked ? (
                <input
                  type="text"
                  value={sanitizeStateCode(form.state_code)}
                  onChange={(e) => setForm({ ...form, state_code: sanitizeStateCode(e.target.value) })}
                  maxLength={2}
                  placeholder="e.g., OD, KA, TN"
                  className="mono"
                />
              ) : (
                <div className="locked-input">
                  <span>{form.state_code || '--'}</span>
                  <span className="lock-pill">Locked after first sale</span>
                </div>
              )}
              <small className="hint">
                This 2-letter state code is used for GST calculations. Once you make your first sale, it will be locked.
              </small>
              {originLocked && firstSaleInfo && (
                <small className="hint">
                  First sale: {firstSaleInfo.invoice_no || 'invoice'} {firstSaleInfo.created_at ? `on ${firstSaleInfo.created_at}` : ''}
                </small>
              )}
            </div>

            <div className="logo-row">
              <div>
                <div className="label">Shop Logo</div>
                {displayLogo ? (
                  <img src={displayLogo} alt="Shop logo" className="logo-preview" />
                ) : (
                  <div className="logo-empty">No logo</div>
                )}
              </div>
              <div className="logo-actions">
                <label className="file-btn">
                  Browse…
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={onFileChange}
                  />
                </label>
                <button type="button" className="btn upload" onClick={uploadLogo} disabled={!logoFile || uploading}>
                  {uploading ? 'Uploading…' : 'Upload'}
                </button>
                {logoPreview && (
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => {
                      setLogoFile(null);
                      setLogoPreview((prev) => {
                        if (prev) URL.revokeObjectURL(prev);
                        return '';
                      });
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="actions">
              {loadFailed && (
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => {
                    setLoading(true);
                    setStatus(null);
                    reloadAll(false);
                  }}
                >
                  Retry
                </button>
              )}
              <button type="submit" className="btn save" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </div>

      <style>{`
        .welcome-hero {
          min-height: calc(100vh - 64px);
          background: radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.16), transparent 35%),
                      radial-gradient(circle at 80% 0%, rgba(16, 185, 129, 0.16), transparent 35%),
                      linear-gradient(135deg, #e0f2fe 0%, #ecfdf3 45%, #e0f2fe 100%);
          padding: 24px 12px 40px;
          display: flex;
          justify-content: center;
          align-items: flex-start;
        }
        .welcome-card {
          width: min(1100px, 100%);
          background: #ffffff;
          border: 1px solid #d8e2f2;
          border-radius: 16px;
          box-shadow: 0 16px 60px rgba(15, 23, 42, 0.16);
          padding: 20px 22px 24px;
        }
        .welcome-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 12px;
        }
        .welcome-eyebrow { font-size: 12px; letter-spacing: 0.08em; color: #0ea5e9; text-transform: uppercase; }
        .welcome-head h1 { margin: 4px 0; font-size: 22px; }
        .welcome-sub { margin: 0; color: #475569; }
        .welcome-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 10px;
          border-radius: 999px;
          background: #f1f5f9;
          color: #0f172a;
          font-weight: 600;
          font-size: 14px;
        }
        .dot { width: 10px; height: 10px; border-radius: 999px; background: #22c55e; display: inline-block; }
        .welcome-form { display: grid; gap: 12px; }
        .grid.two { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); }
        .field { display: flex; flex-direction: column; gap: 6px; }
        label { font-weight: 600; color: #0f172a; font-size: 14px; }
        .req { color: #b91c1c; margin-left: 4px; }
        input, textarea {
          width: 100%;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          padding: 10px 12px;
          background: #fff;
          font-size: 14px;
          color: #0f172a;
          box-sizing: border-box;
        }
        textarea { resize: vertical; min-height: 110px; }
        .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; text-transform: uppercase; }
        .hint { color: #475569; display: block; margin-top: 2px; }
        .logo-row { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; padding: 12px; border: 1px dashed #cbd5e1; border-radius: 12px; background: #f8fafc; }
        .logo-preview { width: 110px; height: 110px; object-fit: contain; border: 1px solid #cbd5e1; border-radius: 10px; background: #fff; }
        .logo-empty { width: 110px; height: 110px; display: grid; place-items: center; border: 1px dashed #cbd5e1; border-radius: 10px; color: #94a3b8; font-size: 12px; background: #fff; }
        .logo-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .file-btn {
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid #cbd5e1;
          background: #fff;
          cursor: pointer;
          font-weight: 600;
          color: #0f172a;
        }
        .file-btn input {
          position: absolute;
          inset: 0;
          opacity: 0;
          cursor: pointer;
        }
        .btn {
          border: none;
          border-radius: 10px;
          padding: 10px 16px;
          font-weight: 700;
          cursor: pointer;
        }
        .btn.upload { background: #059669; color: #fff; }
        .btn.upload:disabled { opacity: 0.7; cursor: not-allowed; }
        .btn.ghost { background: #f8fafc; color: #0f172a; border: 1px solid #cbd5e1; }
        .actions { display: flex; justify-content: flex-end; margin-top: 4px; }
        .btn.save { background: #059669; color: #fff; min-width: 96px; }
        .btn.save:disabled { opacity: 0.7; cursor: not-allowed; }
        .welcome-alert {
          border-radius: 10px;
          padding: 10px 12px;
          font-weight: 600;
          margin-bottom: 10px;
          border: 1px solid transparent;
        }
        .alert-error { background: #fff1f2; border-color: #fecdd3; color: #b91c1c; }
        .alert-success { background: #ecfdf3; border-color: #bbf7d0; color: #166534; }
        .loading { padding: 20px 12px; color: #475569; }
        .locked-input {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          padding: 10px 12px;
          background: #f8fafc;
          font-weight: 700;
        }
        .lock-pill {
          background: #e2e8f0;
          padding: 6px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          color: #0f172a;
        }
        @media (max-width: 640px) {
          .welcome-card { padding: 16px; }
          .welcome-head { flex-direction: column; align-items: flex-start; }
          .logo-row { align-items: flex-start; }
        }
      `}</style>
    </div>
  );
}
